/** Permanent character growth is an award ledger, never an additive saved total. */
export const DUNGEON_CHARACTER_STORAGE_KEY = "masterTypingDungeonCharacterV1";
export const DUNGEON_CHARACTER_SCHEMA_VERSION = 1 as const;
export const DUNGEON_CHARACTER_ATTRIBUTES = [
  "strength", "vitality", "dexterity", "intelligence",
] as const;
export type DungeonCharacterAttribute = typeof DUNGEON_CHARACTER_ATTRIBUTES[number];
export type DungeonCharacterStats = Record<DungeonCharacterAttribute, number>;
export interface DungeonCharacterVictory {
  runId: string;
  completedAt: string;
  training: DungeonCharacterAttribute | null;
  trainingRevision: number;
}
export interface DungeonCharacterProfile {
  schemaVersion: 1;
  name: "Vault Warden";
  victories: DungeonCharacterVictory[];
}
export interface DungeonCharacterBonuses {
  strengthDamage: number;
  maximumVitality: number;
  swiftWindowMs: number;
  mechanismWindowMs: number;
  arcaneProtection: number;
}
export interface DungeonCharacterInspection {
  status: "ready" | "empty" | "future" | "corrupt";
  profile: DungeonCharacterProfile | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
function validAttribute(value: unknown): value is DungeonCharacterAttribute {
  return DUNGEON_CHARACTER_ATTRIBUTES.includes(value as DungeonCharacterAttribute);
}
function validRunId(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= 200 &&
    value.trim() === value && !/[\u0000-\u001f\u007f]/.test(value);
}
function isoDate(value: unknown): string | null {
  if (typeof value !== "string" || value.length > 64) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : null;
}
function emptyCharacter(): DungeonCharacterProfile {
  return { schemaVersion: 1, name: "Vault Warden", victories: [] };
}
function order(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}
function unionVictories(victories: readonly DungeonCharacterVictory[]): DungeonCharacterVictory[] {
  const byRun = new Map<string, DungeonCharacterVictory>();
  for (const entry of victories) {
    const previous = byRun.get(entry.runId);
    if (!previous) {
      byRun.set(entry.runId, { ...entry });
      continue;
    }
    // Revision resolves recovered over-cap allocations. Concurrent first choices
    // use a fixed order, independent of device clock or merge argument order.
    const winner = entry.trainingRevision > previous.trainingRevision ||
      (entry.trainingRevision === previous.trainingRevision &&
        order(entry.training ?? "~", previous.training ?? "~") < 0)
      ? entry : previous;
    byRun.set(entry.runId, {
      ...winner,
      completedAt: previous.completedAt < entry.completedAt ? previous.completedAt : entry.completedAt,
    });
  }
  return [...byRun.values()].sort((a, b) => order(a.completedAt, b.completedAt) || order(a.runId, b.runId));
}

/** Inspect before persisting. Future or damaged documents must be retained intact. */
export function inspectDungeonCharacter(value: unknown): DungeonCharacterInspection {
  if (value === undefined || value === null) return { status: "empty", profile: emptyCharacter() };
  if (isRecord(value) && typeof value.schemaVersion === "number" && value.schemaVersion > 1) {
    return { status: "future", profile: null };
  }
  if (!isRecord(value) || value.schemaVersion !== 1 || value.name !== "Vault Warden" || !Array.isArray(value.victories)) {
    return { status: "corrupt", profile: null };
  }
  const victories: DungeonCharacterVictory[] = [];
  for (const entry of value.victories) {
    if (!isRecord(entry) || !validRunId(entry.runId) ||
        (entry.training !== null && !validAttribute(entry.training)) ||
        !Number.isSafeInteger(entry.trainingRevision) || (entry.trainingRevision as number) < 0 ||
        (entry.training === null ? entry.trainingRevision !== 0 : (entry.trainingRevision as number) < 1)) {
      return { status: "corrupt", profile: null };
    }
    const completedAt = isoDate(entry.completedAt);
    if (!completedAt) return { status: "corrupt", profile: null };
    victories.push({
      runId: entry.runId, completedAt, training: entry.training as DungeonCharacterAttribute | null,
      trainingRevision: entry.trainingRevision as number,
    });
  }
  return { status: "ready", profile: { ...emptyCharacter(), victories: unionVictories(victories) } };
}

/** Display fallback only; inspect first before any write to an existing key. */
export function normalizeDungeonCharacter(value: unknown): DungeonCharacterProfile {
  return inspectDungeonCharacter(value).profile ?? emptyCharacter();
}
function requireCharacter(value: unknown): DungeonCharacterProfile {
  const inspection = inspectDungeonCharacter(value);
  if (!inspection.profile) throw new Error(`Dungeon character data is ${inspection.status}; the original must be preserved.`);
  return inspection.profile;
}

export function mergeDungeonCharacters(left: unknown, right: unknown): DungeonCharacterProfile {
  const a = requireCharacter(left);
  const b = requireCharacter(right);
  return { ...emptyCharacter(), victories: unionVictories([...a.victories, ...b.victories]) };
}

function allocation(profile: DungeonCharacterProfile) {
  const stats: DungeonCharacterStats = { strength: 10, vitality: 10, dexterity: 10, intelligence: 10 };
  const available: DungeonCharacterVictory[] = [];
  for (const entry of profile.victories) {
    if (entry.training && stats[entry.training] < 20) stats[entry.training] += 1;
    else available.push(entry);
  }
  return { stats, available };
}

export function getDungeonCharacterStats(profile: unknown): DungeonCharacterStats {
  return allocation(requireCharacter(profile)).stats;
}
export function getDungeonCharacterProgress(profile: unknown): {
  level: number; completedMazes: number; trainingPoints: number; fullyTrained: boolean;
} {
  const normalized = requireCharacter(profile);
  const { stats, available } = allocation(normalized);
  return {
    level: normalized.victories.length + 1,
    completedMazes: normalized.victories.length,
    trainingPoints: available.length,
    fullyTrained: DUNGEON_CHARACTER_ATTRIBUTES.every((attribute) => stats[attribute] === 20),
  };
}

export function getDungeonCharacterBonuses(stats?: Partial<DungeonCharacterStats> | null): DungeonCharacterBonuses {
  const improvement = (attribute: DungeonCharacterAttribute) =>
    typeof stats?.[attribute] === "number" && Number.isFinite(stats[attribute])
      ? Math.min(10, Math.max(0, Math.floor(stats[attribute]) - 10)) : 0;
  return {
    strengthDamage: improvement("strength"),
    maximumVitality: improvement("vitality") * 5,
    swiftWindowMs: improvement("dexterity") * 100,
    mechanismWindowMs: improvement("intelligence") * 150,
    arcaneProtection: Math.min(3, Math.floor(improvement("intelligence") / 3)),
  };
}

export function awardDungeonCharacterVictory(
  profile: unknown,
  victory: { runId: string; completedAt: string },
): DungeonCharacterProfile {
  const next = requireCharacter(profile);
  if (!validRunId(victory.runId)) throw new Error("A character victory requires a valid run ID.");
  const completedAt = isoDate(victory.completedAt);
  if (!completedAt) throw new Error("A character victory requires a valid completion time.");
  if (next.victories.some((entry) => entry.runId === victory.runId)) return next;
  return {
    ...next,
    victories: unionVictories([...next.victories, { runId: victory.runId, completedAt, training: null, trainingRevision: 0 }]),
  };
}

export function trainDungeonCharacter(profile: unknown, attribute: DungeonCharacterAttribute): DungeonCharacterProfile {
  const next = requireCharacter(profile);
  if (!validAttribute(attribute)) return next;
  const { stats, available } = allocation(next);
  const entry = available[0];
  if (!entry || stats[attribute] >= 20 || entry.trainingRevision >= Number.MAX_SAFE_INTEGER) return next;
  return {
    ...next,
    victories: next.victories.map((item) => item.runId === entry.runId
      ? { ...item, training: attribute, trainingRevision: item.trainingRevision + 1 }
      : item),
  };
}
